"use client";
import React, { useCallback, useRef, useEffect } from "react";
import type { TableColumn } from "./TableHeader";
import useStoredState from "../../hooks/useStoredState";

export function useColumnResize(columns: TableColumn[], storageKey?: string) {
  const [storedWidths, setStoredWidths] = useStoredState<Record<string, number>>(storageKey, {});

  const widths = columns.map(col => {
    const stored = col.name ? storedWidths[col.name] : undefined;
    return stored !== undefined ? stored : col.minWidth;
  });

  const dragRef = useRef<{
    colName: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragRef.current) return;
      const { colName, startX, startWidth } = dragRef.current;
      const delta = e.clientX - startX;
      setStoredWidths(prev => ({ ...prev, [colName]: Math.max(80, startWidth + delta) }));
    },
    [setStoredWidths]
  );

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  const getResizeHandleProps = useCallback(
    (colIndex: number) => ({
      onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const colName = columns[colIndex]?.name;
        if (!colName) return;
        dragRef.current = {
          colName,
          startX: e.clientX,
          startWidth: widths[colIndex],
        };
        document.addEventListener("pointermove", onPointerMove);
        document.addEventListener("pointerup", onPointerUp);
      },
    }),
    [widths, columns, onPointerMove, onPointerUp]
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  return { widths, getResizeHandleProps };
}
